import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search as SearchIcon,
  Home as HomeIcon,
  Briefcase,
  Layers,
  Workflow,
  DollarSign,
  Mail,
  UserCircle2,
  ExternalLink,
  type LucideIcon,
} from "lucide-react";
import { cn } from "@nexirt/ui";
import { getOrCreateSessionId } from "../lib/copilot-session";

/**
 * CommandPalette — ⌘K modal per Navigation.html command-palette artboard
 * (Rev 25 Phase 2 Loop E).
 *
 * Two groups:
 *   1. "Ask the assistant" (primary) — when input has content, top row
 *      routes the typed query to the hero CopilotComposer's session via
 *      'nexirt:copilot:preload' with a single user-turn array; ChatWidget
 *      opens with the message pre-loaded ready to send (user can edit).
 *   2. "Pages" — Home / Work / Services / Process / Pricing / Contact /
 *      Portal (when authed). react-router-dom navigate.
 *
 * Triggers:
 *   - ⌘K / Ctrl+K global keybinding (respects activeElement to avoid
 *     hijacking textareas/inputs OUTSIDE the palette).
 *   - Click on the header's ⌘K mock trigger (Layout.tsx).
 *
 * Keyboard:
 *   - ↑↓ navigate rows
 *   - Enter activates highlighted row
 *   - Esc closes; outside-click on scrim closes
 *   - Focus-trapped to input on open
 */

type PageRow = {
  label: string;
  to: string;
  description: string;
  icon: LucideIcon;
  authedOnly?: boolean;
};

const PAGES: PageRow[] = [
  { label: "Home", to: "/", description: "/", icon: HomeIcon },
  { label: "Work", to: "/work", description: "Case studies & shipped systems", icon: Briefcase },
  { label: "Services", to: "/services", description: "What we build", icon: Layers },
  { label: "Process", to: "/process", description: "5-stage delivery model", icon: Workflow },
  { label: "Pricing", to: "/pricing", description: "Project + Care tiers", icon: DollarSign },
  { label: "Contact", to: "/contact", description: "Start a project", icon: Mail },
  { label: "Portal", to: "/portal", description: "Client + admin", icon: UserCircle2, authedOnly: true },
];

export function CommandPalette({ authed }: { authed: boolean }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const scrimRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();

  const visiblePages = PAGES.filter(
    (p) => !p.authedOnly || authed,
  ).filter((p) =>
    query.trim()
      ? `${p.label} ${p.description}`
          .toLowerCase()
          .includes(query.trim().toLowerCase())
      : true,
  );

  const askRow = query.trim().length > 0;
  const totalRows = (askRow ? 1 : 0) + visiblePages.length;

  // Global ⌘K / Ctrl+K listener
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const isCmdK =
        (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k";
      if (!isCmdK) return;
      const active = document.activeElement;
      const tag = active?.tagName?.toLowerCase();
      const editable =
        tag === "input" ||
        tag === "textarea" ||
        (active as HTMLElement | null)?.isContentEditable;
      if (editable && active !== inputRef.current) return;
      e.preventDefault();
      setOpen((v) => !v);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Also listen for the openCommandPalette() KeyboardEvent the existing
  // footer ⌘K trigger + the new header ⌘K trigger dispatch (rev 21 +
  // rev 25 Phase 2 Loop C wired this). It's a synthesized KeyboardEvent
  // so our `onKey` above already handles it — no extra listener needed.

  // Focus input on open + reset state
  useEffect(() => {
    if (open) {
      setActiveIdx(0);
      requestAnimationFrame(() => inputRef.current?.focus());
    } else {
      setQuery("");
    }
  }, [open]);

  // Body scroll-lock when open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // Esc closes
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.stopPropagation();
        setOpen(false);
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIdx((i) => (i + 1) % Math.max(totalRows, 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIdx((i) =>
          (i - 1 + Math.max(totalRows, 1)) % Math.max(totalRows, 1),
        );
      } else if (e.key === "Enter") {
        e.preventDefault();
        commitActiveRow();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, totalRows, activeIdx, askRow, visiblePages, query]);

  const onScrimClick = useCallback((e: React.MouseEvent) => {
    if (e.target === scrimRef.current) setOpen(false);
  }, []);

  function commitActiveRow() {
    if (askRow && activeIdx === 0) {
      askAssistant(query.trim());
      return;
    }
    const idx = askRow ? activeIdx - 1 : activeIdx;
    const page = visiblePages[idx];
    if (!page) return;
    navigate(page.to);
    setOpen(false);
  }

  function askAssistant(prompt: string) {
    const sessionId = getOrCreateSessionId();
    // Pre-load the widget with a single user turn; widget renders it
    // ready to send (user can edit or hit Enter). The composer-inline
    // flow is reserved for the hero; CommandPalette is a "fire and
    // open the widget" shortcut.
    window.dispatchEvent(
      new CustomEvent("nexirt:copilot:open", { detail: { prompt } }),
    );
    setOpen(false);
    // No router navigate — the widget overlay is already a modal.
    void sessionId;
  }

  if (!open) return null;

  return (
    <div
      ref={scrimRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="cmdk-input"
      data-command-palette
      onMouseDown={onScrimClick}
      className="fixed inset-0 z-[var(--nx-z-cmdk)] flex items-start justify-center bg-black/60 px-4 pt-[12vh] backdrop-blur-sm"
    >
      <div
        className={cn(
          "w-full max-w-[580px] overflow-hidden rounded-nx3 border border-nx-line-strong",
          "shadow-[0_30px_80px_-30px_rgba(0,0,0,0.7)] backdrop-blur-xl",
        )}
        style={{
          background:
            "linear-gradient(180deg, rgba(17,19,27,0.96), rgba(14,16,24,0.96))",
        }}
      >
        <div className="flex items-center gap-2.5 border-b border-nx-line px-4 py-3.5">
          <SearchIcon className="h-4 w-4 shrink-0 text-nx-text-faint" strokeWidth={1.8} />
          <input
            ref={inputRef}
            id="cmdk-input"
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setActiveIdx(0);
            }}
            placeholder="Ask the assistant or jump to a page…"
            className="flex-1 bg-transparent text-sm text-nx-text placeholder:text-nx-text-faint focus:outline-none"
            aria-label="Search"
          />
          <span className="font-mono text-[10.5px] text-nx-text-faint inline-flex items-center gap-0.5 rounded-[5px] border border-nx-line-strong bg-nx-surface px-1.5 py-0.5">
            ESC
          </span>
        </div>

        <div className="max-h-[60vh] overflow-y-auto py-2">
          {askRow ? (
            <>
              <div className="px-4 pt-2 pb-1 font-mono text-[10px] uppercase tracking-[0.18em] text-nx-text-faint">
                Ask the assistant
              </div>
              <button
                type="button"
                role="option"
                aria-selected={activeIdx === 0}
                onClick={() => askAssistant(query.trim())}
                onMouseEnter={() => setActiveIdx(0)}
                data-cmdk-row="ask"
                className={cn(
                  "flex w-full items-center gap-2.5 border-l-2 px-4 py-2.5 text-left transition-colors",
                  activeIdx === 0
                    ? "border-nx-accent bg-nx-accent-soft"
                    : "border-transparent hover:bg-white/[0.03]",
                )}
              >
                <span
                  className="grid h-[26px] w-[26px] shrink-0 place-items-center rounded-[7px]"
                  style={{
                    background:
                      "conic-gradient(from 0deg, var(--nx-accent), var(--nx-accent-2), var(--nx-accent))",
                  }}
                >
                  <span
                    className="block h-[18px] w-[18px] rounded-full"
                    style={{ background: "var(--nx-bg-raised)" }}
                  />
                </span>
                <div className="flex-1 text-[13.5px] text-nx-text">
                  <span className="font-mono text-nx-text-faint">›</span>{" "}
                  <span className="italic">"{query.trim()}"</span>
                </div>
                <span className="font-mono text-[10.5px] text-nx-text-faint">↵</span>
              </button>
            </>
          ) : null}

          <div className="px-4 pt-3 pb-1 font-mono text-[10px] uppercase tracking-[0.18em] text-nx-text-faint">
            Pages
          </div>
          {visiblePages.length === 0 ? (
            <p className="px-4 py-3 text-[12.5px] text-nx-text-faint">
              No pages match. Press ↵ to ask the assistant instead.
            </p>
          ) : (
            visiblePages.map((p, i) => {
              const rowIdx = askRow ? i + 1 : i;
              const Icon = p.icon;
              return (
                <button
                  key={p.to}
                  type="button"
                  role="option"
                  aria-selected={activeIdx === rowIdx}
                  onClick={() => {
                    navigate(p.to);
                    setOpen(false);
                  }}
                  onMouseEnter={() => setActiveIdx(rowIdx)}
                  data-cmdk-row="page"
                  className={cn(
                    "flex w-full items-center gap-2.5 border-l-2 px-4 py-2.5 text-left transition-colors",
                    activeIdx === rowIdx
                      ? "border-nx-accent bg-nx-accent-soft"
                      : "border-transparent hover:bg-white/[0.03]",
                  )}
                >
                  <span className="grid h-[24px] w-[24px] shrink-0 place-items-center rounded-[6px] border border-nx-line bg-nx-surface text-nx-accent">
                    <Icon className="h-3.5 w-3.5" strokeWidth={1.8} />
                  </span>
                  <div className="flex-1 leading-tight">
                    <div className="text-[13.5px] text-nx-text">{p.label}</div>
                    <div className="font-mono text-[10.5px] text-nx-text-faint">
                      {p.description}
                    </div>
                  </div>
                  <ExternalLink className="h-3 w-3 text-nx-text-faint" strokeWidth={2} />
                </button>
              );
            })
          )}
        </div>

        <div className="flex items-center gap-4 border-t border-nx-line bg-nx-bg/40 px-4 py-2.5 font-mono text-[10.5px] text-nx-text-faint">
          <span>↑↓ navigate</span>
          <span>↵ select</span>
          <span>esc close</span>
          <span className="ml-auto text-nx-success">● assistant ready</span>
        </div>
      </div>
    </div>
  );
}

export default CommandPalette;
